/* James Crossing: mural fit calculator. Reuses the theme's <modal-dialog> drawer.
   Reads the mural's standard size from the product's spec text (custom.roll_dimensions /
   custom.pattern_repeat) or from an optional override metafield custom.mural_size ("420 x 300 cm"). */

const NUM = "(\\d+(?:[.,]\\d+)?)";
const UNIT = "(cm|mm|metres?|meters?|m)(?![a-z])";
const LEN = NUM + "\\s*" + UNIT;

function toCm(n, u) {
  const v = parseFloat(String(n).replace(",", "."));
  u = (u || "cm").toLowerCase();
  if (u === "mm") return v / 10;
  if (u === "cm") return v;
  return v * 100; // m, metre(s), meter(s)
}

/* Lower-case, drop imperial in brackets such as "(118 in)" or "(9.2 ft)", normalise "×". */
export function clean(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/[×✕]/g, "x")
    .replace(/\(\s*[\d.,\/\s]+\s*(?:in|inch|inches|ft|feet|foot|"|″|'|”)\.?\s*\)/g, " ")
    .replace(/&nbsp;|\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function panelCount(t) {
  let m;
  if ((m = t.match(/set of (\d+) rolls? (?:with|of|containing) (\d+) panels?/))) return +m[1] * +m[2];
  if ((m = t.match(/(\d+) rolls? (?:with|of|containing) (\d+) panels? each/))) return +m[1] * +m[2];
  if ((m = t.match(/(?:each )?roll contains (\d+) panels?/))) return +m[1];
  if ((m = t.match(/comes in (\d+) panels?/))) return +m[1];
  if ((m = t.match(/(?:set of |in )?(\d+)[ -]panels? mural/))) return +m[1];
  if ((m = t.match(/set of (\d+) panels?/))) return +m[1];
  return null;
}

/* "H 300 cm x W 420 cm" / "W 420 cm x H 300 cm" (explicit overall labels before the numbers) */
function labelledOverall(t) {
  let m = t.match(new RegExp("\\bh(?:eight)?\\s*:?\\s*" + LEN + "\\s*(?:x|by|,)\\s*w(?:idth)?\\s*:?\\s*" + LEN));
  if (m) return { w: toCm(m[3], m[4]), h: toCm(m[1], m[2]) };
  m = t.match(new RegExp("\\bw(?:idth)?\\s*:?\\s*" + LEN + "\\s*(?:x|by|,)\\s*h(?:eight)?\\s*:?\\s*" + LEN));
  if (m) return { w: toCm(m[1], m[2]), h: toCm(m[3], m[4]) };
  return null;
}

/**
 * Returns { ok:true, w, h, source, note } in cm, or { ok:false, reason }.
 */
export function parseMuralSize({ override, roll, repeat } = {}) {
  // 1. Optional structured override: custom.mural_size, e.g. "420 x 300 cm" or "W 420 cm x H 300 cm"
  const o = clean(override);
  if (o) {
    const lab = labelledOverall(o);
    if (lab) return { ok: true, ...lab, source: "custom.mural_size" };
    const m = o.match(/(\d+(?:[.,]\d+)?)\s*(cm|mm|m)?\s*(?:x|by)\s*(\d+(?:[.,]\d+)?)\s*(cm|mm|m)?/);
    if (m) {
      const u2 = m[4] || m[2] || "cm", u1 = m[2] || u2;
      return { ok: true, w: toCm(m[1], u1), h: toCm(m[3], u2), source: "custom.mural_size" };
    }
  }
  const r = clean(roll), p = clean(repeat), all = r + " | " + p;

  // 2. Panel murals: "Set of 2 rolls with 3 panels each. Each panel is 70 cm W x 300 cm H"
  const panelRx = new RegExp(
    "(?:each )?panel (?:is|measures|size:?)\\s*:?\\s*(?:w(?:idth)?\\s*)?" + LEN +
    "\\s*(?:w|wide|width|\\(w\\))?\\s*(?:x|by)\\s*(?:h(?:eight)?\\s*)?" + LEN
  );
  const pm = all.match(panelRx) || all.match(new RegExp("each panel is\\s+" + LEN + "\\s*(?:w(?:idth)?|wide)?\\s*(?:x|by)\\s*" + LEN));
  if (pm) {
    const pw = toCm(pm[1], pm[2]), ph = toCm(pm[3], pm[4]);
    const n = panelCount(all);
    const lab = labelledOverall(p) || labelledOverall(r);
    if (n) {
      const w = Math.round(n * pw * 10) / 10;
      let note = n + " panels of " + pw + " x " + ph + " cm";
      if (lab && (Math.abs(lab.w - w) > 2 || Math.abs(lab.h - ph) > 2)) {
        note += "; overall size given as " + lab.w + " x " + lab.h + " cm (used)";
        return { ok: true, w: lab.w, h: lab.h, source: "pattern_repeat", note, mismatch: true };
      }
      return { ok: true, w, h: ph, source: "panels", note };
    }
    if (lab) return { ok: true, w: lab.w, h: lab.h, source: "pattern_repeat", note: "panel count not stated" };
    return { ok: false, reason: "panel size found but not how many panels" };
  }

  // 3. Single panel: "Width 140 cm by 300 cm Panel", "140 cm by 3 m panel"
  const sp = r.match(new RegExp("(?:width\\s*)?" + LEN + "\\s*(?:w(?:ide)?|\\(w\\))?\\s*(?:x|by)\\s*(?:height\\s*)?" + LEN + "\\s*(?:h(?:igh)?|\\(h\\))?\\s*panel\\b"));
  if (sp && !/panels/.test(r.slice(0, sp.index + sp[0].length + 2))) {
    return { ok: true, w: toCm(sp[1], sp[2]), h: toCm(sp[3], sp[4]), source: "single panel" };
  }

  // 4. Roll / by-the-metre products have no fixed mural size
  if (/running\s*met|per\s*met|by the met/.test(all)) return { ok: false, reason: "sold by the running metre (no fixed size)" };
  if (/\broll\b/.test(r)) return { ok: false, reason: "sold by the roll (no fixed mural size stated)" };
  return { ok: false, reason: "no mural size found" };
}

/* ---------- UI ---------- */
const CM_PER_IN = 2.54;
const MAX_WALLS = 4;
const r1 = (x) => Math.round(x * 10) / 10;
const fmt = (x) => { const v = r1(x); return Number.isInteger(v) ? String(v) : v.toFixed(1); };
const ceilTo = (x, step) => Math.ceil(x / step - 1e-9) * step;
const both = (cmW, cmH, first) => {
  const m = `${fmt(cmW)} × ${fmt(cmH)} cm`, i = `${fmt(cmW / CM_PER_IN)} × ${fmt(cmH / CM_PER_IN)} in`;
  return first === "in" ? `${i} (${m})` : `${m} (${i})`;
};
const bothHtml = (cmW, cmH, first) => {
  const m = `${fmt(cmW)} × ${fmt(cmH)} cm`, i = `${fmt(cmW / CM_PER_IN)} × ${fmt(cmH / CM_PER_IN)} in`;
  const [a, b] = first === "in" ? [i, m] : [m, i];
  return `<span class="mural-fit__size-main">${a}</span> <span class="mural-fit__size-alt">(${b})</span>`;
};
const one = (cm, first) => first === "in" ? `${fmt(cm / CM_PER_IN)} in (${fmt(cm)} cm)` : `${fmt(cm)} cm (${fmt(cm / CM_PER_IN)} in)`;
const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

/* Shared state for other tools on the page.
   window.muralFit.state is always the latest snapshot; listen for "muralfit:change" on document for updates.
   All lengths are in centimetres; `unit` is the unit the customer is typing in. */
function publish(state) {
  const api = (window.muralFit = window.muralFit || {});
  api.version = 2;
  api.state = state;
  api.getState = () => api.state;
  api.onChange = (cb) => { const h = (e) => cb(e.detail); document.addEventListener("muralfit:change", h); return () => document.removeEventListener("muralfit:change", h); };
  document.dispatchEvent(new CustomEvent("muralfit:change", { detail: state }));
}

/* Hide the "How much do I need?" roll estimator where the fit calculator shows, and only there.
   The snippet adds a pending <style> on mural pages so the estimator doesn't flash; we keep it or remove it here. */
function settleEstimator(showMural) {
  const pending = document.querySelectorAll("style[data-mfc-est-pending]");
  document.querySelectorAll(".product-single__box__block--estimator").forEach((b) => { b.hidden = !!showMural; b.style.display = showMural ? "none" : ""; });
  if (showMural) {
    // "Use our calculator" links elsewhere on the page open the fit calculator instead
    document.querySelectorAll('a[href$="#wallpaper-estimator"]:not([data-a11y-dialog-show])').forEach((a) => { a.setAttribute("href", "#mural-fit-calculator"); a.dataset.mfcRedirected = "1"; });
  } else {
    pending.forEach((s) => s.remove());
  }
  window.__mfcSettled = true;
}

/* ---------- Drawer scrolling ----------
   The theme's <modal-dialog> locks page scroll from its "opening" event, but that handler is not attached
   in some browsers (it is bound after the element moves itself into #moved-modal-elements, so Firefox
   never runs it). Then the mouse wheel scrolls the page behind the drawer. This keeps the drawer
   self-sufficient: it watches aria-hidden to lock/unlock page scroll itself, keeps wheel scrolling inside
   the drawer, and lifts the footer above Shopify's theme-preview bar. */
function jcDrawerScroll(modal, scroller, opts = {}) {
  if (!modal || !scroller || modal.dataset.jcScrollFix) return;
  modal.dataset.jcScrollFix = "1";
  if (!document.getElementById("jc-drawer-lock-css")) {
    const st = document.createElement("style"); st.id = "jc-drawer-lock-css";
    st.textContent = "html.jc-scroll-lock,html.jc-scroll-lock body{overflow:hidden!important;overscroll-behavior:none}html.jc-scroll-lock{scrollbar-gutter:stable}";
    document.head.appendChild(st);
  }
  const locks = (window.__jcDrawerLocks = window.__jcDrawerLocks || new Set());
  const isOpen = () => modal.isConnected && modal.getAttribute("aria-hidden") !== "true" && modal.hasAttribute("aria-modal");
  const previewBar = () => document.getElementById("PBarNextFrameWrapper") || document.getElementById("preview-bar-iframe");
  const updateInset = () => {
    let inset = 0; const pb = previewBar();
    if (pb) { const r = pb.getBoundingClientRect(), cs = getComputedStyle(pb); if (cs.display !== "none" && cs.visibility !== "hidden" && r.height > 0 && r.top < innerHeight) inset = Math.max(0, Math.min(200, innerHeight - r.top)); }
    modal.style.setProperty(opts.insetVar || "--jc-bottom-inset", Math.round(inset) + "px");
  };
  let wasOpen = false;
  const sync = () => {
    const open = isOpen();
    if (open === wasOpen) return;
    wasOpen = open;
    const key = modal.id || modal;
    if (open) { locks.add(key); document.documentElement.classList.add("jc-scroll-lock"); updateInset(); opts.onOpen && opts.onOpen(); }
    else { locks.delete(key); if (!locks.size) document.documentElement.classList.remove("jc-scroll-lock"); }
  };
  new MutationObserver(sync).observe(modal, { attributes: true, attributeFilter: ["aria-hidden", "aria-modal"] });
  sync();
  // Wheel / trackpad: scroll the drawer, never the page behind it.
  const doc = modal.querySelector('[role="document"]') || modal;
  modal.addEventListener("wheel", (e) => {
    if (!isOpen() || e.ctrlKey) return; // ctrl+wheel = browser zoom
    let dy = e.deltaY; if (!dy) return;
    if (e.deltaMode === 1) dy *= 16; else if (e.deltaMode === 2) dy *= scroller.clientHeight;
    // a scrollable element inside the drawer (e.g. a long list) that can still move gets the native scroll
    for (let n = e.target; n && n !== scroller && n !== modal && n.nodeType === 1; n = n.parentElement) {
      if (n.scrollHeight > n.clientHeight + 1 && /(auto|scroll)/.test(getComputedStyle(n).overflowY) && (dy > 0 ? n.scrollTop < n.scrollHeight - n.clientHeight - 1 : n.scrollTop > 0)) return;
    }
    const max = scroller.scrollHeight - scroller.clientHeight;
    const can = dy > 0 ? scroller.scrollTop < max - 1 : scroller.scrollTop > 0;
    if (can && scroller.contains(e.target)) return; // normal native scroll of the drawer content
    e.preventDefault(); // would otherwise chain to the page
    if (can && doc.contains(e.target)) scroller.scrollTop += dy; // over the drawer header/footer: scroll the content
  }, { passive: false });
  window.addEventListener("resize", () => { if (isOpen()) updateInset(); }, { passive: true });
  const insetCbs = (window.__jcInsetCbs = window.__jcInsetCbs || new Set()); insetCbs.add(updateInset);
  const all = () => insetCbs.forEach((f) => f());
  const hookBar = () => {
    const pb = previewBar(); if (!pb) return false; if (pb.dataset.jcWatched) { updateInset(); return true; } pb.dataset.jcWatched = "1";
    if (window.ResizeObserver) { const ro = new ResizeObserver(all); ro.observe(pb); [...pb.children].forEach((c) => ro.observe(c)); }
    new MutationObserver(all).observe(pb, { attributes: true, subtree: true, attributeFilter: ["style", "class", "hidden"] });
    all(); return true;
  };
  if (!hookBar()) { let n = 0; const t = setInterval(() => { if (hookBar() || ++n > 20) clearInterval(t); }, 500); }
  // Sticky footer: it sits directly after the content and only pins to the bottom (with a divider)
  // while the content is taller than the drawer.
  const footer = opts.footer;
  if (footer) {
    let raf = 0;
    const update = () => {
      raf = 0;
      const overflow = scroller.scrollHeight > scroller.clientHeight + 1;
      const pinTo = scroller.getBoundingClientRect().bottom - (parseFloat(getComputedStyle(footer).bottom) || 0);
      const stuck = overflow && Math.abs(footer.getBoundingClientRect().bottom - pinTo) < 1.5;
      footer.classList.toggle("is-stuck", stuck);
    };
    const queue = () => { if (!raf) raf = requestAnimationFrame(update); };
    scroller.addEventListener("scroll", queue, { passive: true });
    window.addEventListener("resize", queue, { passive: true });
    if (window.ResizeObserver) { const ro = new ResizeObserver(queue); ro.observe(scroller); Array.from(scroller.children).forEach((c) => ro.observe(c)); }
    new MutationObserver(queue).observe(scroller, { childList: true, subtree: true, attributes: true, attributeFilter: ["hidden", "class", "style"] });
    new MutationObserver(queue).observe(modal, { attributes: true, attributeFilter: ["aria-hidden"] });
    queue();
  }
  updateInset();
  return { updateInset, isOpen };
}

class MuralFitCalculator {
  constructor(el) {
    this.el = el;
    const d = el.dataset;
    this.q = (s) => el.querySelector(s);
    this.size = parseMuralSize({ override: d.muralSize, roll: d.rollDimensions, repeat: d.patternRepeat });
    this.trigger = document.querySelector(`[data-mural-fit-trigger="${el.id}"]`);
    this.title = d.productTitle || document.title;
    this.url = d.productUrl || location.href.split("?")[0];
    if (!this.size.ok) {
      if (this.trigger) this.trigger.hidden = true;
      el.dataset.mfcUnparsed = this.size.reason;
      settleEstimator(false);
      publish({ ok: false, reason: this.size.reason, product: { title: this.title, url: this.url } });
      return;
    }
    if (this.trigger) this.trigger.hidden = false;
    settleEstimator(true);
    const num = (v, def) => { const n = parseFloat(v); return Number.isFinite(n) && n >= 0 ? n : def; };
    this.allowCm = num(d.allowanceCm, 5); this.allowIn = num(d.allowanceIn, 2);
    this.cornerCm = num(d.cornerCm, 0); this.cornerIn = num(d.cornerIn, 0);
    this.email = d.email || "info@jamescrossing.ca";
    this.formId = d.formId;
    try { this.variants = JSON.parse(this.q("[data-mfc-variants]")?.textContent || "[]"); } catch (e) { this.variants = []; }
    this.unit = "cm";
    this.rows = [...el.querySelectorAll("[data-mfc-wall]")];
    this.h = this.q("[data-mfc-h]");
    this.result = this.q("[data-mfc-result]"); this.quote = this.q("[data-mfc-quote]");
    this.std = this.q("[data-mfc-standard]"); this.diagram = this.q("[data-mfc-diagram]");
    this.addBtn = this.q("[data-mfc-add-wall]");
    this.std.innerHTML = bothHtml(this.size.w, this.size.h, "cm");
    el.querySelectorAll("[data-mfc-unit]").forEach((b) => b.addEventListener("click", () => this.setUnit(b.dataset.mfcUnit)));
    this.inputs().forEach((i) => {
      i.addEventListener("input", () => this.calc());
      // Enter in any measurement box runs the check and brings the answer into view
      i.addEventListener("keydown", (e) => { if (e.key === "Enter" && !e.isComposing) { e.preventDefault(); this.calc(true); this.reveal(); } });
    });
    this.addBtn?.addEventListener("click", () => this.addWall());
    el.querySelectorAll("[data-mfc-wall-remove]").forEach((b) => b.addEventListener("click", () => this.removeWall(b.closest("[data-mfc-wall]"))));
    this.scroller = this.q(".mural-fit__scroll");
    this.q("[data-mfc-form]").addEventListener("submit", (e) => { e.preventDefault(); this.calc(true); this.reveal(); });
    el.addEventListener("on:modal:opening", () => { document.body.classList.add("u-scroll-disabled"); this.updateQuote(); this.toTop(); });
    this.drawer = jcDrawerScroll(el, this.scroller, { footer: this.q(".mural-fit__footer"), insetVar: "--mfc-bottom-inset", onOpen: () => { this.updateQuote(); this.toTop(); } });
    // anything the customer does in the drawer after it opens stops the "back to the top" resets below
    ["wheel", "touchstart", "pointerdown", "keydown"].forEach((t) => el.addEventListener(t, () => { this.userScrolled = true; }, { passive: true }));
    this.setUnit("cm", true);
    this.syncWallUi();
    this.calc();
  }
  /* Every open starts at the top (standard size and wall width in view), even if it was scrolled to the answer last
     time. Repeated after the slide-in animation and focus handling, unless the customer has already scrolled or typed. */
  toTop() {
    const sc = this.scroller; if (!sc) return;
    this.userScrolled = false;
    const go = () => { if (!this.userScrolled && this.drawer?.isOpen?.() !== false) sc.scrollTop = 0; };
    sc.scrollTop = 0;
    requestAnimationFrame(() => { go(); requestAnimationFrame(go); });
    clearTimeout(this.topTimer); this.topTimer = setTimeout(go, 450);
  }
  /* Scroll the drawer so the answer (or the "please enter" message) is in view. */
  reveal() {
    const sc = this.scroller; if (!sc) return;
    const target = this.result && this.result.innerHTML.trim() ? this.result : null;
    if (!target) return;
    const a = document.activeElement;
    if (a && a.matches && a.matches("input") && window.matchMedia && window.matchMedia("(pointer: coarse)").matches) a.blur(); // close the phone keyboard
    const top = target.getBoundingClientRect().top - sc.getBoundingClientRect().top + sc.scrollTop - 12;
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    sc.scrollTo({ top: Math.max(0, top), behavior: reduce ? "auto" : "smooth" });
  }
  inputs() { return [...this.el.querySelectorAll("[data-mfc-w]"), this.h]; }
  visibleRows() { return this.rows.filter((r) => !r.hidden); }
  allowance() { return this.unit === "in" ? this.allowIn * CM_PER_IN : this.allowCm; }
  corner() { return this.unit === "in" ? this.cornerIn * CM_PER_IN : this.cornerCm; }
  allowanceText() { return this.unit === "in" ? `${fmt(this.allowIn)} in (${fmt(this.allowIn * CM_PER_IN)} cm)` : `${fmt(this.allowCm)} cm (${fmt(this.allowCm / CM_PER_IN)} in)`; }
  cornerText() { return this.unit === "in" ? `${fmt(this.cornerIn)} in (${fmt(this.cornerIn * CM_PER_IN)} cm)` : `${fmt(this.cornerCm)} cm (${fmt(this.cornerCm / CM_PER_IN)} in)`; }
  addWall() {
    const next = this.rows.find((r) => r.hidden);
    if (!next) return;
    next.hidden = false;
    this.syncWallUi();
    next.querySelector("[data-mfc-w]")?.focus();
    this.calc();
  }
  removeWall(row) {
    if (!row || row === this.rows[0]) return;
    // keep the list in order: shift later values up, then hide the last visible row
    const vis = this.visibleRows(), idx = vis.indexOf(row);
    for (let i = idx; i < vis.length - 1; i++) vis[i].querySelector("[data-mfc-w]").value = vis[i + 1].querySelector("[data-mfc-w]").value;
    const last = vis[vis.length - 1]; last.querySelector("[data-mfc-w]").value = ""; last.hidden = true;
    this.syncWallUi();
    (vis[Math.max(0, idx - 1)] || this.rows[0]).querySelector("[data-mfc-w]")?.focus();
    this.calc();
  }
  syncWallUi() {
    const n = this.visibleRows().length, multi = n > 1;
    this.rows.forEach((r, i) => {
      const lab = r.querySelector("[data-mfc-wall-label]");
      if (lab) lab.textContent = multi ? `Wall ${i + 1} width` : "Wall width";
    });
    if (this.addBtn) {
      this.addBtn.hidden = n >= MAX_WALLS;
      this.addBtn.textContent = multi ? "+ Add another wall" : "+ Wrapping around more than one wall?";
      this.addBtn.setAttribute("aria-label", multi ? `Add wall ${n + 1}` : "Wrapping around more than one wall? Add a wall");
    }
    const hl = this.q("[data-mfc-h-label]"); if (hl) hl.textContent = multi ? "Height (tallest wall)" : "Wall height";
    const hint = this.q("[data-mfc-hint-multi]"); if (hint) hint.hidden = !multi;
    const count = this.q("[data-mfc-wall-count]"); if (count) count.textContent = multi ? `${n} walls` : "";
  }
  setUnit(u, initial) {
    if (u !== "cm" && u !== "in") return;
    const prev = this.unit; this.unit = u;
    this.el.querySelectorAll("[data-mfc-unit]").forEach((b) => { const on = b.dataset.mfcUnit === u; b.classList.toggle("is-on", on); b.setAttribute("aria-pressed", on ? "true" : "false"); });
    this.el.querySelectorAll("[data-mfc-unit-label]").forEach((s) => (s.textContent = u === "in" ? "in" : "cm"));
    this.inputs().forEach((i) => {
      i.placeholder = i.dataset[u === "in" ? "phIn" : "phCm"];
      if (!initial && prev !== u && i.value) {
        const v = parseFloat(i.value);
        if (v > 0) {
          // convert from the exact centimetres while the box still shows our last conversion, so cm -> in -> cm gives back 260, not 260.1
          const cm = i.dataset.mfcCm && i.dataset.mfcShown === i.value ? +i.dataset.mfcCm : prev === "cm" ? v : v * CM_PER_IN;
          i.value = fmt(u === "cm" ? cm : cm / CM_PER_IN);
          i.dataset.mfcCm = cm; i.dataset.mfcShown = i.value;
        }
      }
    });
    this.q("[data-mfc-allow]").textContent = this.allowanceText();
    this.std.innerHTML = bothHtml(this.size.w, this.size.h, u);
    if (!initial) this.calc();
  }
  read(input) { const v = parseFloat(String(input?.value ?? "").replace(",", ".")); return v > 0 ? (this.unit === "in" ? v * CM_PER_IN : v) : null; }
  variantTitle() {
    const form = this.formId && document.getElementById(this.formId);
    const id = form ? parseInt(form.querySelector('input[name="id"]')?.value, 10) : NaN;
    const v = this.variants.find((x) => x.id === id);
    if (!v) return "";
    return v.options.filter((o) => !/^(sample|roll|panel|default title)$/i.test(o) && !/superwide|width$/i.test(o)).join(" / ");
  }
  baseState() {
    return { ok: true, product: { title: this.title, url: this.url, colour: this.variantTitle() }, unit: this.unit,
      standard: { widthCm: this.size.w, heightCm: this.size.h },
      trimAllowanceCm: this.allowance(), cornerAllowanceCm: this.corner(), maxWalls: MAX_WALLS };
  }
  calc(submitted) {
    const vis = this.visibleRows();
    const walls = vis.map((r, i) => ({ index: i + 1, widthCm: this.read(r.querySelector("[data-mfc-w]")) }));
    const H = this.read(this.h);
    const filled = walls.filter((w) => w.widthCm);
    this.rec = null;
    const finish = (html) => {
      this.result.innerHTML = html; this.renderDiagram(); this.updateQuote();
      publish({ ...this.baseState(), walls: walls.map((w) => ({ ...w })), heightCm: H, ...(this.rec ? this.rec.state : { complete: false }) });
    };
    if (this.inputs().some((i) => i.validity?.badInput && !i.closest("[hidden]"))) {
      return finish(`<div class="mural-fit__msg mural-fit__msg--err">Please type numbers only, for example ${this.unit === "in" ? "157" : "400"}. Choose centimetres or inches with the buttons above.</div>`);
    }
    if (filled.length < walls.length || !H) {
      const multi = walls.length > 1;
      return finish(submitted ? `<div class="mural-fit__msg mural-fit__msg--err">Please enter ${multi ? "each wall's width and the height" : "your wall's width and height"}.</div>` : "");
    }
    if (walls.some((w) => w.widthCm > 5000) || H > 2000) return finish(`<div class="mural-fit__msg mural-fit__msg--err">Those measurements look very large. Please check the units (${this.unit === "in" ? "inches" : "centimetres"}).</div>`);
    const A = this.allowance(), C = this.corner(), mw = this.size.w, mh = this.size.h, u = this.unit;
    const multi = walls.length > 1, corners = walls.length - 1;
    const wallsSum = walls.reduce((s, w) => s + w.widthCm, 0);
    const W = wallsSum + corners * C; // total run to cover
    const needW = W + 2 * A, needH = H + 2 * A;
    // recommended custom size: run + trim allowance on the outer edges / top and bottom, rounded up (1 cm, or 0.5 in)
    const recW = u === "in" ? ceilTo(needW / CM_PER_IN, 0.5) * CM_PER_IN : ceilTo(needW, 1);
    const recH = u === "in" ? ceilTo(needH / CM_PER_IN, 0.5) * CM_PER_IN : ceilTo(needH, 1);
    const exW = mw - W, exH = mh - H;
    const fits = mw >= needW - 0.05 && mh >= needH - 0.05;
    // Layout along the mural actually ordered (standard if it fits, otherwise the recommended custom size), run centred
    const stripW = fits ? mw : recW, offset = (stripW - W) / 2;
    let x = offset; const segs = [], cornerPos = [];
    walls.forEach((w, i) => {
      segs.push({ index: w.index, widthCm: w.widthCm, fromCm: x, toCm: x + w.widthCm });
      x += w.widthCm;
      if (i < corners) { cornerPos.push({ index: i + 1, between: [w.index, w.index + 1], fromLeftCm: x + C / 2 }); x += C; }
    });
    const runLabel = multi ? `your ${walls.length} walls (${one(W, u)} in total)` : "your wall";
    let html;
    if (fits) {
      const lots = exW > Math.max(30, mw * 0.2) || exH > Math.max(30, mh * 0.2);
      html = `<div class="mural-fit__msg mural-fit__msg--ok"><p class="mural-fit__verdict">The standard size fits ${multi ? "around your walls" : "your wall"}.</p>
        <p>You'll have about <strong>${one(exW, u)}</strong> extra in width and <strong>${one(exH, u)}</strong> extra in height for your installer to trim off.</p>
        ${lots ? `<p>That's quite a lot to trim, so some of the design will be cut away. If you'd like to keep more of it, ask us about a custom size of <strong>${both(recW, recH, u)}</strong>.</p>` : ""}</div>`;
    } else {
      const short = [], tight = [];
      if (mw < W) short.push(`too narrow by ${one(W - mw, u)}`); else if (mw < needW - 0.05) tight.push("width");
      if (mh < H) short.push(`too short by ${one(H - mh, u)}`); else if (mh < needH - 0.05) tight.push("height");
      let sentence = short.length ? `It's ${short.join(" and ")}${multi ? ` for ${runLabel}` : ""}` : `It covers ${runLabel}`;
      if (tight.length) sentence += `${short.length ? ", and" : ", but"} leaves less than ${this.allowanceText()} per side for trimming on the ${tight.join(" and ")}`;
      html = `<div class="mural-fit__msg mural-fit__msg--warn"><p class="mural-fit__verdict">The standard size won't quite fit.</p>
        <p>${sentence}.</p></div>`;
    }
    const wallList = multi
      ? `<ul class="mural-fit__rec-walls">${walls.map((w) => `<li>Wall ${w.index}: ${one(w.widthCm, u)}</li>`).join("")}<li>Height: ${one(H, u)}</li><li><strong>Total width: ${one(W, u)}</strong>${corners && C ? ` (includes ${corners} × ${this.cornerText()} for corners)` : ""}</li></ul>`
      : "";
    html += `<div class="mural-fit__rec"><span class="mural-fit__rec-label">${fits ? "Custom size, if you'd prefer" : "Recommended custom size"}</span>
      <span class="mural-fit__rec-size">${bothHtml(recW, recH, u)}</span>
      ${wallList}
      <span class="mural-fit__rec-note">${multi ? "Your total wall width" : "Your wall"} plus ${this.allowanceText()} on ${multi ? "the outer edges, top and bottom" : "every side"} for trimming.</span></div>`;
    this.rec = { walls, H, W, recW, recH, fits, stripW, offset, segs, cornerPos,
      state: { complete: true, totalWidthCm: W, wallsWidthCm: wallsSum, corners: cornerPos, segments: segs, needed: { widthCm: needW, heightCm: needH },
        fits, recommended: { widthCm: recW, heightCm: recH }, layout: { muralWidthCm: stripW, muralHeightCm: fits ? mh : recH, usesStandardSize: fits, offsetCm: offset } } };
    finish(html);
  }
  renderDiagram() {
    const box = this.diagram; if (!box) return;
    const r = this.rec;
    if (!r || r.walls.length < 2) { box.innerHTML = ""; box.hidden = true; return; }
    box.hidden = false;
    const u = this.unit, VW = 460, PADX = 10, top = 26, h = 70, scale = (VW - 2 * PADX) / r.stripW;
    const X = (cm) => PADX + cm * scale;
    const shades = ["#efe6dc", "#e4ebf0", "#f3e1dd", "#e6eee4"];
    let svg = `<svg class="mural-fit__strip" viewBox="0 0 ${VW} ${top + h + 34}" role="img" aria-labelledby="mfc-strip-title mfc-strip-desc" preserveAspectRatio="xMidYMid meet">
      <title id="mfc-strip-title">Where each wall falls on the mural</title>
      <desc id="mfc-strip-desc">${esc(r.segs.map((s) => `Wall ${s.index} from ${one(s.fromCm, u)} to ${one(s.toCm, u)}`).join("; "))}</desc>
      <rect x="${X(0)}" y="${top}" width="${r.stripW * scale}" height="${h}" fill="#faf7f3" stroke="#333D47" stroke-width="1"/>`;
    // trim zones at the outer edges
    svg += `<rect x="${X(0)}" y="${top}" width="${Math.max(0, r.offset * scale)}" height="${h}" fill="url(#mfc-hatch)"/>`;
    svg += `<rect x="${X(r.offset + r.W)}" y="${top}" width="${Math.max(0, (r.stripW - r.offset - r.W) * scale)}" height="${h}" fill="url(#mfc-hatch)"/>`;
    svg = svg.replace("<rect", `<defs><pattern id="mfc-hatch" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><rect width="6" height="6" fill="#faf7f3"/><line x1="0" y1="0" x2="0" y2="6" stroke="#C86B60" stroke-opacity=".35" stroke-width="2"/></pattern></defs><rect`);
    r.segs.forEach((s, i) => {
      const x0 = X(s.fromCm), wpx = s.widthCm * scale;
      svg += `<rect x="${x0}" y="${top}" width="${wpx}" height="${h}" fill="${shades[i % 4]}"/>`;
      svg += `<text x="${x0 + wpx / 2}" y="${top + h / 2 - 3}" text-anchor="middle" class="mural-fit__strip-wall">Wall ${s.index}</text>`;
      svg += `<text x="${x0 + wpx / 2}" y="${top + h / 2 + 13}" text-anchor="middle" class="mural-fit__strip-dim">${u === "in" ? fmt(s.widthCm / CM_PER_IN) + " in" : fmt(s.widthCm) + " cm"}</text>`;
    });
    r.cornerPos.forEach((c, i) => {
      const cx = X(c.fromLeftCm), lab = u === "in" ? `${fmt(c.fromLeftCm / CM_PER_IN)} in` : `${fmt(c.fromLeftCm)} cm`;
      svg += `<line x1="${cx}" y1="${top - 6}" x2="${cx}" y2="${top + h + 6}" stroke="#C86B60" stroke-width="2" stroke-dasharray="5 3"/>`;
      svg += `<text x="${cx}" y="${i % 2 ? top + h + 20 : top - 10}" text-anchor="middle" class="mural-fit__strip-corner">Corner ${c.index}: ${lab}</text>`;
    });
    svg += `<rect x="${X(0)}" y="${top}" width="${r.stripW * scale}" height="${h}" fill="none" stroke="#333D47" stroke-width="1"/>`;
    svg += `<text x="${X(0)}" y="${top + h + (r.cornerPos.length > 1 ? 32 : 20)}" class="mural-fit__strip-dim">0</text><text x="${X(r.stripW)}" y="${top + h + (r.cornerPos.length > 1 ? 32 : 20)}" text-anchor="end" class="mural-fit__strip-dim">${u === "in" ? fmt(r.stripW / CM_PER_IN) + " in" : fmt(r.stripW) + " cm"}</text></svg>`;
    const list = r.cornerPos.map((c) => `<li>Corner ${c.index} (wall ${c.between[0]} to wall ${c.between[1]}): <strong>${one(c.fromLeftCm, u)}</strong> from the left edge</li>`).join("");
    box.innerHTML = `<p class="mural-fit__rec-label">Where your corners fall on the ${r.fits ? "standard" : "recommended custom"} mural (${both(r.stripW, r.fits ? this.size.h : r.recH, u)})</p>
      ${svg}
      <ul class="mural-fit__corners">${list}</ul>
      <p class="mural-fit__strip-note">Measured from the mural's left edge, with the design centred on your walls. Hatched ends are trimmed off${r.cornerPos.length && this.corner() ? `; each corner allows ${this.cornerText()}` : ""}.</p>`;
  }
  updateQuote() {
    if (!this.quote) return;
    const u = this.unit, colour = this.variantTitle();
    const lines = [
      "Hello James Crossing,", "",
      "I'd like a quote for a custom size of this mural.", "",
      `Mural: ${this.title}`,
      colour ? `Colour: ${colour}` : null,
      `Link: ${this.url}`,
      `Standard size: ${both(this.size.w, this.size.h, u)}`,
    ];
    const r = this.rec;
    if (r && r.walls.length > 1) {
      lines.push(`My walls (the mural wraps around ${r.walls.length} walls):`);
      r.walls.forEach((w) => lines.push(`  Wall ${w.index} width: ${one(w.widthCm, u)}`));
      lines.push(`  Height (tallest wall): ${one(r.H, u)}`);
      lines.push(`  Total width: ${one(r.W, u)}${r.cornerPos.length && this.corner() ? ` (includes ${r.cornerPos.length} × ${this.cornerText()} for corners)` : ""}`);
      lines.push(`Recommended custom size: ${both(r.recW, r.recH, u)}`);
      lines.push(`(total width plus ${this.allowanceText()} on the outer edges, top and bottom for trimming)`);
      lines.push(`Corners from the mural's left edge: ${r.cornerPos.map((c) => one(c.fromLeftCm, u)).join(", ")}`);
    } else if (r) {
      lines.push(`My wall: ${both(r.W, r.H, u)}`);
      lines.push(`Recommended custom size: ${both(r.recW, r.recH, u)}`);
      lines.push(`(my wall plus ${this.allowanceText()} on every side for trimming)`);
    } else {
      lines.push("My wall: (width x height)");
    }
    lines.push("", "Thank you,", "");
    const subject = `Custom mural size quote: ${this.title}`;
    this.quote.href = `mailto:${this.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines.filter((l) => l !== null).join("\r\n"))}`;
  }
}

function boot() {
  document.querySelectorAll("modal-dialog.mural-fit:not([data-mfc-ready])").forEach((el) => { el.dataset.mfcReady = "1"; new MuralFitCalculator(el); });
}
if (typeof document !== "undefined") {
  boot();
  document.addEventListener("shopify:section:load", boot);
  document.addEventListener("click", (e) => {
    const a = e.target.closest && e.target.closest('a[href$="#mural-fit-calculator"]');
    if (!a || a.hasAttribute("data-a11y-dialog-show")) return;
    const el = document.getElementById("mural-fit-calculator");
    if (el && typeof el.open === "function") { e.preventDefault(); el.open(); }
  });
}
